/**
 * FeedbackQueue — admin user-feedback triage queue (STORY-010-005).
 *
 * Tabs: Open / Replied / Closed / Spam (Segmented). The Spam tab flips
 * `include_spam=true` and shows `status=open` rows with a high spam score.
 * Rows open a detail drawer with the reply composer + close/reopen.
 */
import { useMemo, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { Button, GPill, Segmented, Skeleton } from "@garden/ui";
import { registerAdminI18n, ADMIN_NS } from "@/features/admin/api";
import { useFeedbackList } from "@/features/admin/feedback/hooks";
import type { FeedbackListItem } from "@/features/admin/feedback/api";
import { FeedbackDrawer } from "@/features/admin/feedback/FeedbackDrawer";

const TABS = ["open", "replied", "closed", "spam"] as const;
type Tab = (typeof TABS)[number];

function statusTone(status: string): "warn" | "success" | "neutral" {
  switch (status) {
    case "open":
      return "warn";
    case "replied":
      return "success";
    default:
      return "neutral";
  }
}

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function truncate(text: string, max = 80): string {
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

export function FeedbackQueue(): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);

  const [tab, setTab] = useState<Tab>("open");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // The Spam tab is open rows with include_spam=true; other tabs filter by status.
  const { status, includeSpam } = useMemo(() => {
    if (tab === "spam") return { status: "open", includeSpam: true };
    return { status: tab, includeSpam: false };
  }, [tab]);

  const { data, isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useFeedbackList(status, includeSpam);

  const allItems: FeedbackListItem[] = data?.pages.flatMap((p) => p.items) ?? [];
  // On the spam tab, surface only the high-score rows (the default queue hides
  // them; the spam tab includes them — show the ones that were hidden).
  const items =
    tab === "spam" ? allItems.filter((i) => i.spam_score >= 80) : allItems;

  return (
    <div data-testid="feedback-queue-page" className="flex flex-col gap-4">
      <h1 className="text-xl font-bold">{t("feedback.title")}</h1>

      <div role="group" aria-label={t("feedback.filter.status")}>
        <Segmented
          options={TABS.map((tb) => ({ value: tb, label: t(`feedback.tab.${tb}`) }))}
          value={tab}
          onChange={(v) => setTab(v as Tab)}
        />
      </div>

      {isLoading ? (
        <div>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="mb-2 h-12 w-full rounded-lg" />
          ))}
        </div>
      ) : isError ? (
        <p className="text-sm text-[var(--g-error)]">{t("error.loadFailed")}</p>
      ) : items.length === 0 ? (
        <p data-testid="feedback-empty" className="py-8 text-center text-sm text-[var(--g-secondary)]">
          {t("feedback.empty")}
        </p>
      ) : (
        <>
          <div className="hidden sm:block overflow-x-auto rounded-[var(--g-radius-md)] border border-[var(--g-hairline)]">
            <table data-testid="feedback-table" className="w-full min-w-[600px] border-collapse bg-[var(--g-surface)] text-sm">
              <thead className="bg-[var(--g-elevated)]">
                <tr>
                  <th scope="col" className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]">
                    {t("feedback.columns.createdAt")}
                  </th>
                  <th scope="col" className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]">
                    {t("feedback.columns.message")}
                  </th>
                  <th scope="col" className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]">
                    {t("feedback.columns.rating")}
                  </th>
                  <th scope="col" className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]">
                    {t("feedback.columns.status")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr
                    key={item.id}
                    data-testid={`feedback-row-${item.id}`}
                    onClick={() => setSelectedId(item.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSelectedId(item.id);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label={truncate(item.message)}
                    className="cursor-pointer border-b border-[var(--g-hairline)] hover:bg-[var(--g-elevated)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--g-primary)]"
                  >
                    <td className="min-h-[44px] px-3 py-3 text-sm text-[var(--g-secondary)]">{formatDate(item.created_at)}</td>
                    <td className="px-3 py-3 text-sm font-medium">{truncate(item.message)}</td>
                    <td className="px-3 py-3 text-sm text-[var(--g-secondary)]">{item.rating ?? "—"}</td>
                    <td className="px-3 py-3">
                      <GPill tone={statusTone(item.status)} size="sm">
                        {t(`feedback.status.${item.status}`, { defaultValue: item.status })}
                      </GPill>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-2 sm:hidden" data-testid="feedback-card-stack">
            {items.map((item) => (
              <div
                key={item.id}
                data-testid={`feedback-card-${item.id}`}
                onClick={() => setSelectedId(item.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setSelectedId(item.id);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={truncate(item.message)}
                className="cursor-pointer rounded-[var(--g-radius)] border border-[var(--g-hairline)] bg-[var(--g-surface)] p-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="text-sm font-medium">{truncate(item.message)}</div>
                  <GPill tone={statusTone(item.status)} size="sm">
                    {t(`feedback.status.${item.status}`, { defaultValue: item.status })}
                  </GPill>
                </div>
                <div className="mt-1 text-xs text-[var(--g-secondary)]">{formatDate(item.created_at)}</div>
              </div>
            ))}
          </div>

          {hasNextPage ? (
            <Button
              variant="secondary"
              size="sm"
              className="mx-auto"
              disabled={isFetchingNextPage}
              onClick={() => void fetchNextPage()}
            >
              {isFetchingNextPage ? t("queue.refreshing") : t("queue.loadMore")}
            </Button>
          ) : null}
        </>
      )}

      <FeedbackDrawer feedbackId={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  );
}

export default FeedbackQueue;
